// Servicio de documentos: upload, details, presigned download y helpers
// Respeta ApiService y buildEndpointUrl existentes

import apiService from '../service/apiService';
import apiConfig, { buildEndpointUrl, PROXY_CONFIG, LOGGING_CONFIG } from 'config/apiConfig';
import { appConfig } from 'config/keycloak';
// COMENTADO - Solo usar token de Keycloak, no AuthService para client_credentials
// import authService from '../service/AuthService.ts';
import axios from 'axios';

// MIME types permitidos por negocio
export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/svg+xml',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

// Tamaño máximo por defecto (MB) – si existe config global, puede sobreescribirse desde el componente
export const DEFAULT_MAX_FILE_SIZE_MB = 25; // TODO: alinear con backend si hay valor global

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const isTransientError = (status) => {
  return status === 408 || status === 425 || status === 429 || (status >= 500 && status <= 599);
};

const serializeTagsToCsv = (tags) => {
  if (!tags) return '';
  if (Array.isArray(tags)) {
    return tags
      .map((t) => (typeof t === 'string' ? t : (t?.label || t?.value || ''))) // admite chips como objetos simples
      .filter(Boolean)
      .join(',');
  }
  if (typeof tags === 'string') return tags;
  return '';
};

export const parseContentDisposition = (headerValue) => {
  if (!headerValue) return undefined;
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(headerValue);
  try {
    const encoded = match?.[1];
    if (encoded) return decodeURIComponent(encoded);
    const simple = match?.[2];
    return simple || undefined;
  } catch (_) {
    return undefined;
  }
};

export const inferFilename = (details, documentId, contentType) => {
  if (details?.name) return details.name;
  const extByType = {
    'application/pdf': 'pdf',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'text/plain': 'txt',
    'text/csv': 'csv',
  };
  const ext = extByType[contentType] || 'bin';
  return `document-${documentId}.${ext}`;
};

const validateFile = (file, { maxSizeMB = DEFAULT_MAX_FILE_SIZE_MB, allowedTypes = ALLOWED_MIME_TYPES } = {}) => {
  if (!file) {
    throw new Error('No se seleccionó ningún archivo.');
  }
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`Tipo de archivo no permitido: ${file.type || 'desconocido'}`);
  }
  const maxBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxBytes) {
    throw new Error(`El archivo supera el tamaño máximo (${maxSizeMB} MB).`);
  }
};

// Upload con progreso/cancelación usando XMLHttpRequest para soportar onUploadProgress
export const uploadDocument = async ({
  file,
  category,
  originType,
  originId,
  description,
  tags,
  signal,
  onUploadProgress,
  maxSizeMB,
  allowedTypes,
  retryAttempts = 2,
}) => {
  validateFile(file, { maxSizeMB, allowedTypes });

  let endpoint = buildEndpointUrl('DOCUMENTS', apiConfig.ENDPOINTS.DOCUMENTS.upload);
  const url = `${appConfig.baseUrl || ''}${endpoint}`;
  // Usar endpoint tal cual devuelve buildEndpointUrl (igual que AddressApi)
  const tagsCsv = serializeTagsToCsv(tags);

  const doOnce = async () => {
    const form = new FormData();
    form.append('file', file);
    form.append('category', category);
    form.append('originType', originType);
    form.append('originId', originId);
    form.append('description', description || '');
    form.append('tags', tagsCsv || '');

    // Progreso con fetch no es estándar; mantenemos onUploadProgress no-op para compatibilidad
    if (typeof onUploadProgress === 'function') {
      try { onUploadProgress({ loaded: 0, total: file.size || 0, percent: 0 }); } catch (_) {}
    }

    // Patrón solicitado: obtener token explícitamente y usarlo en la petición
    // Preferir token de usuario (Keycloak) si está disponible; si no, client_credentials
    let token = null;
    try {
      const userToken = (typeof window !== 'undefined' && window.__kc_getToken) ? window.__kc_getToken() : null;
      token = userToken || await apiService.getToken();;
    } catch (_) {
      token = await apiService.getToken();
    }

    const resp = await axios.post(url, form, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
      onUploadProgress: (evt) => {
        if (typeof onUploadProgress === 'function' && evt && evt.total) {
          const percent = Math.round((evt.loaded / evt.total) * 100);
          try { onUploadProgress({ loaded: evt.loaded, total: evt.total, percent }); } catch (_) {}
        }
      },
      // En browser axios usa XHR y respeta CORS; no setear Content-Type para FormData
    });

    const data = resp?.data;
    
    // Handle different response formats
    let documentId;
    if (typeof data === 'number') {
      // Backend returns documentId directly as a number
      documentId = data;
    } else if (typeof data === 'object' && data !== null) {
      // Backend returns an object with documentId
      documentId = data.documentId || data.id || data.documentID;
    } else {
      // Fallback
      documentId = data;
    }
    
    console.log('Upload response parsed:', { rawData: data, extractedDocumentId: documentId });
    
    if (typeof onUploadProgress === 'function') {
      try { onUploadProgress({ loaded: file.size || 0, total: file.size || 0, percent: 100 }); } catch (_) {}
    }
    return { documentId, details: data };
  };

  // Retry con backoff para errores transitorios
  let attempt = 0;
  let lastError = null;
  while (attempt <= retryAttempts) {
    try {
      return await doOnce();
    } catch (error) {
      lastError = error;
      const status = error?.status;
      if (error?.__aborted) throw error; // no reintentar si se canceló
      if (!isTransientError(status) || attempt === retryAttempts) {
        throw error;
      }
      const backoffMs = 500 * Math.pow(2, attempt); // 500ms, 1000ms, 2000ms
      if (LOGGING_CONFIG.enabled) {
        console.warn(`Upload transitorio (status ${status}). Reintentando en ${backoffMs}ms...`);
      }
      await sleep(backoffMs);
      attempt += 1;
    }
  }
  throw lastError || new Error('Fallo de subida desconocido');
};

export const getDocumentDetails = async (documentId, { signal } = {}) => {
  if (!documentId) throw new Error('documentId es requerido');
  let endpoint = buildEndpointUrl('DOCUMENTS', apiConfig.ENDPOINTS.DOCUMENTS.details(documentId));
  if (PROXY_CONFIG.enabled && /^https?:\/\//i.test(endpoint)) {
    endpoint = endpoint.replace(/^https?:\/\/[^/]+/i, '');
  }
  // Mantener URL tal cual
  const response = await apiService.request(endpoint, { method: 'GET', signal });
  const data = (response && response.data) ? response.data : response;
  // Adapter seguro
  return {
    documentId: data?.documentId || data?.id || documentId,
    name: data?.name || data?.fileName || data?.filename,
    contentType: data?.contentType || data?.mimeType || data?.type,
    size: data?.size || data?.length || undefined,
    createdAt: data?.createdAt || data?.created_at || data?.createdDate || undefined,
    raw: data,
  };
};

export const getPresignedDownloadUrl = async (documentId, { signal } = {}) => {
  if (!documentId) throw new Error('documentId es requerido');
  let endpoint = buildEndpointUrl('DOCUMENTS', apiConfig.ENDPOINTS.DOCUMENTS.download(documentId));
  // Mantener URL tal cual
  const response = await apiService.request(endpoint, { method: 'GET', signal });
  const data = (response && response.data) ? response.data : response;
  
  // Adapter seguro para URL presignada
  const url = data?.presignedUrl || data?.url || data?.downloadUrl;
  if (!url) {
    throw new Error('La respuesta no contiene una URL presignada');
  }
  
  return {
    url,
    filename: data?.fileName || data?.filename || data?.name,
    expiresAt: data?.expiresAt || data?.expires_at,
    ttlSeconds: data?.ttlSeconds || data?.ttl_seconds,
    raw: data,
  };
};

export const getPreviewResource = async (documentId, { signal } = {}) => {
  if (!documentId) throw new Error('documentId es requerido');
  
  // Usar la nueva función de apiService que construye URLs absolutas
  const resp = await apiService.requestWithBlob(apiConfig.ENDPOINTS.DOCUMENTS.preview(documentId), {
    signal,
    redirect: 'manual'
  });

  console.log('resp', resp);
  
  // 302 con Location (redirect a URL presignada)
  if (resp.status === 302 || resp.status === 301) {
    const location = resp.headers.get('Location') || resp.headers.get('location');
    if (location) return { url: location };
  }

  // Respuesta exitosa
  if (resp.ok) {
    const ct = resp.headers.get('content-type') || '';
    
    // Si es JSON, extraer URL de preview
    if (ct.includes('application/json')) {
      const data = await resp.json().catch(() => ({}));
      const url = data?.previewUrl || data?.presignedUrl || data?.url;
      const filename = data?.fileName || data?.filename;
      if (url) return { url, filename, expiresAt: data?.expiresAt, ttlSeconds: data?.ttlSeconds };
      const err = new Error('La respuesta JSON no contiene una URL de previsualización');
      err.status = resp.status;
      throw err;
    } else {
      // Si es blob/binario, convertir a archivo previsualizable
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition');
      const filename = parseContentDisposition(cd);
      
      // Crear URL del blob para preview
      const objectUrl = URL.createObjectURL(blob);
      
      return { 
        blob, 
        filename, 
        contentType: blob.type,
        objectUrl, // URL para mostrar en iframe/img/video
        size: blob.size
      };
    }
  }

  // Error
  const text = await resp.text().catch(() => '');
  const err = new Error(`HTTP ${resp.status}: ${text || 'Error obteniendo preview del documento'}`);
  err.status = resp.status;
  throw err;
};

// Eliminar documento por id usando endpoint /documents/:documentId/delete
export const deleteDocument = async (documentId) => {
  if (!documentId) throw new Error('documentId es requerido');
  let endpoint = buildEndpointUrl('DOCUMENTS', apiConfig.ENDPOINTS.DOCUMENTS.delete(documentId));
  // Mantener URL tal cual
  // Token consistente - Solo usar token de Keycloak del usuario autenticado
  const token = await apiService.getToken();
  // Backend requiere POST para /:id/delete
  const url = `${appConfig.baseUrl || ''}${endpoint}`;
  const resp = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
  return resp?.data || { success: true };
};

// Ejecuta una función con URL presignada; si falla por expiración, reintenta una vez
export const withFreshUrl = async (documentId, consumerFn) => {
  const first = await getPresignedDownloadUrl(documentId);
  try {
    return await consumerFn(first);
  } catch (e) {
    if (e?.status === 401 || e?.status === 403) {
      const second = await getPresignedDownloadUrl(documentId);
      return consumerFn(second);
    }
    throw e;
  }
};

export const searchDocuments = async ({ page = 0, size = 20, sortBy = 'id', sortDirection = 'desc', filters = [], originId, originType, signal } = {}) => {
  let endpoint = buildEndpointUrl('DOCUMENTS', apiConfig.ENDPOINTS.DOCUMENTS.search);
  if (PROXY_CONFIG.enabled && /^https?:\/\//i.test(endpoint)) {
    endpoint = endpoint.replace(/^https?:\/\/[^/]+/i, '');
  }
  // Mantener URL tal cual

  // Agregar filtros implícitos por contexto si vienen
  const effectiveFilters = Array.isArray(filters) ? [...filters] : [];
  if (originId != null) {
    effectiveFilters.push({ field: 'origin_id', operator: '=', value: String(originId) });
  }
  if (originType) {
    effectiveFilters.push({ field: 'origin_type', operator: '=', value: String(originType) });
  }

  const payload = { page, size, sortBy, sortDirection, filters: effectiveFilters };
  const response = await apiService.request(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  const data = (response && response.data) ? response.data : (response || {});

  // Normalizar respuesta mínima para la lista
  const items = Array.isArray(data?.content) ? data.content : (Array.isArray(data?.items) ? data.items : []);
  const pageInfo = {
    page: data?.page ?? page,
    size: data?.size ?? size,
    totalElements: data?.totalElements ?? data?.total ?? items.length,
    totalPages: data?.totalPages ?? undefined,
  };

  const normalize = (d) => ({
    documentId: d?.documentId || d?.id,
    name: d?.name || d?.fileName || d?.filename,
    contentType: d?.contentType || d?.mimeType || d?.type,
    size: d?.size,
    createdAt: d?.createdAt || d?.created_at || d?.createdDate,
    originId: d?.originId ?? d?.origin_id,
    originType: d?.originType ?? d?.origin_type,
    description: d?.description,
    tags: d?.tags,
    s3Key: d?.s3Key,
    createdBy: d?.createdBy,
  });

  return { items: items.map(normalize), pageInfo, raw: data };
};

// Lista documentos por origen usando endpoint /documents/origin/:originType/:originId
export const listDocumentsByOrigin = async ({ originType, originId, page = 0, size = 20, sortBy = 'id', sortDirection = 'desc', signal } = {}) => {
  if (!originType || originId == null) throw new Error('originType y originId son requeridos');

  // Construir endpoint con query opcional de paginación si el backend lo soporta
  let endpoint = buildEndpointUrl('DOCUMENTS', `/origin/${originType}/${originId}`);
  const params = new URLSearchParams();
  if (page != null) params.set('page', String(page));
  if (size != null) params.set('size', String(size));
  if (sortBy) params.set('sortBy', String(sortBy));
  if (sortDirection) params.set('sortDirection', String(sortDirection));
  const qs = params.toString();
  if (qs) endpoint += `?${qs}`;

  const response = await apiService.request(endpoint, { method: 'GET', signal });
  const data = (response && response.data) ? response.data : (response || {});

  const items = Array.isArray(data?.content) ? data.content : (Array.isArray(data) ? data : []);
  const pageInfo = {
    page: data?.page ?? page,
    size: data?.size ?? size,
    totalElements: data?.totalElements ?? data?.total ?? items.length,
    totalPages: data?.totalPages ?? undefined,
  };

  const normalize = (d) => ({
    documentId: d?.documentId || d?.id,
    name: d?.name || d?.fileName || d?.filename,
    contentType: d?.contentType || d?.mimeType || d?.type,
    size: d?.size,
    createdAt: d?.createdAt || d?.created_at || d?.createdDate,
    originId: d?.originId ?? d?.origin_id,
    originType: d?.originType ?? d?.origin_type,
    description: d?.description,
    tags: d?.tags,
    s3Key: d?.s3Key,
    createdBy: d?.createdBy,
  });

  return { items: items.map(normalize), pageInfo, raw: data };
};

const documentsApi = {
  uploadDocument,
  getDocumentDetails,
  getPresignedDownloadUrl,
  getPreviewResource,
  withFreshUrl,
  searchDocuments,
  listDocumentsByOrigin,
  deleteDocument,
  parseContentDisposition,
  inferFilename,
  isTransientError,
  ALLOWED_MIME_TYPES,
  DEFAULT_MAX_FILE_SIZE_MB,
};

export default documentsApi;


